// @ts-check
import { objectEntries, useCustomRef } from '@erpagroapp/common';
import { configsSettings } from './configsSettings';

/** @import { Ref } from 'vue' */
/** @import { GeneralizeSettingsConfigPropertyType } from './types' */
/**
 * @import {
 * SettingsKeys,
 * SettingsFlatten,
 * } from './configsSettings' */

/**
 * @typedef { {
 *  [K in keyof SettingsFlatten]: Ref<GeneralizeSettingsConfigPropertyType<SettingsFlatten[K]['defaultValue']>>
 * }} SettingsValues
 */

/**
 * @internal
 */
export const settingsValues = getInitialValues();

/**
 * @internal
 * @returns {boolean}
 */
export function hasValuesInLocalStorage() {
  const localStorageValues = localStorage.getItem('localSettings');
  return localStorageValues !== null
    && localStorageValues !== '{}';
}

/**
 * @returns {SettingsValues}
 */
function getInitialValues() {
  const initialValues = !hasValuesInLocalStorage()
    ? createValues()
    : mergeValuesWithConfigs(getValuesFromLocalStorage());

  return initialValues;
}

/**
 * @returns {SettingsValues}
 */
function createValues() {
  const valuesTemp = /** @type {Record<SettingsKeys, any>} */ ({});

  for (const [, config] of objectEntries(configsSettings)) {
    for (const [settingKey, { defaultValue }] of objectEntries(config.properties)) {
      valuesTemp[settingKey] = useCustomRef(defaultValue, saveValuesToLocalStorage);
    }
  }

  return valuesTemp;
}

/**
 * @returns {SettingsValues}
 */
function getValuesFromLocalStorage() {
  const storedValues = localStorage.getItem('localSettings');
  let tempValues = /** @type {Record<SettingsKeys, any>} */ ({});

  if (storedValues) {
    tempValues = /** @type {SettingsValues} */ (JSON.parse(storedValues));
  }

  const reactiveValues = /** @type {Record<SettingsKeys, any>} */ ({});
  for (const [settingKey, param] of objectEntries(tempValues)) {
    reactiveValues[settingKey] = useCustomRef(param, saveValuesToLocalStorage);
  }

  return reactiveValues;
}

/**
 * @internal
 * @param {SettingsValues} [values] - Values to be saved.
 * @returns {void}
 */
export function saveValuesToLocalStorage(values) {
  const tempValues = /** @type {Record<SettingsKeys, any>} */ ({});
  const valuedToSave = values ?? settingsValues;

  for (const [settingKey, param] of objectEntries(valuedToSave)) {
    tempValues[settingKey] = param.value;
  }

  localStorage.setItem('localSettings', JSON.stringify(tempValues));
}

/**
 * Merges existing settings from local storage with the current configuration settings.
 * @param {SettingsValues} existingValues - The values retrieved from local storage.
 * @returns {SettingsValues} The merged settings values.
 */
function mergeValuesWithConfigs(existingValues) {
  const mergedValues = /** @type {Record<SettingsKeys, any>} */ ({ ...existingValues });
  let isChanged = false;

  for (const [, config] of objectEntries(configsSettings)) {
    for (const [settingKey, { defaultValue }] of objectEntries(config.properties)) {
      if (!Object.hasOwn(mergedValues, settingKey)) {
        isChanged = true;
        mergedValues[settingKey] = useCustomRef(defaultValue, saveValuesToLocalStorage);
      }
    }
  }

  if (isChanged) {
    saveValuesToLocalStorage(mergedValues);
  }

  return mergedValues;
}
